import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${corporateTheme.backgroundGradient};
  padding: 120px 2rem 4rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 2rem;
  text-align: center;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: 20px;
  padding: 3rem;
  box-shadow: ${corporateTheme.cardShadow};
  border: 1px solid ${corporateTheme.cardBorder};
`;

const CardTitle = styled.h2`
  font-size: 1.8rem;
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  font-weight: 600;
`;

const ContactInfo = styled.div`
  margin-bottom: 2rem;
`;

const InfoLabel = styled.h3`
  font-size: 1.1rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const InfoValue = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const DepartmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const DepartmentCard = styled(motion.div)`
  background: rgba(30, 64, 175, 0.05);
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
    transform: translateY(-5px);
  }
`;

const DepartmentTitle = styled.h3`
  color: ${corporateTheme.primary};
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const DepartmentEmail = styled.p`
  color: ${corporateTheme.textSecondary};
  font-weight: 500;
`;

const BusinessHours = styled.div`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
`;

const HoursTitle = styled.h2`
  font-size: 1.8rem;
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  font-weight: 600;
`;

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const TimeZone = styled.div`
  background: ${corporateTheme.cardBackground};
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
`;

const ZoneTitle = styled.h3`
  color: ${corporateTheme.primary};
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ZoneTime = styled.p`
  color: ${corporateTheme.textSecondary};
  font-weight: 500;
`;

const Contact = () => {
  return (
    <PageContainer>
      <Container>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Contact Us
        </Title>
        
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Get in touch with our team. We're here to help you succeed with affiliate marketing in Eastern Asia.
        </Subtitle>
        
        <ContactGrid>
          <ContactCard
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <CardTitle>Company Information</CardTitle>
            
            <ContactInfo>
              <InfoLabel>Legal Entity</InfoLabel>
              <InfoValue>GrowTech Ltd</InfoValue>
            </ContactInfo>
            
            <ContactInfo>
              <InfoLabel>Headquarters</InfoLabel>
              <InfoValue>Hong Kong</InfoValue>
            </ContactInfo>
            
            <ContactInfo>
              <InfoLabel>General Inquiries</InfoLabel>
              <InfoValue>info@growtech.cc</InfoValue>
            </ContactInfo>
            
            <ContactInfo>
              <InfoLabel>Business Focus</InfoLabel>
              <InfoValue>
                Digital affiliate marketing platform specializing in e-commerce, SaaS, 
                and digital services for Eastern Asian markets.
              </InfoValue>
            </ContactInfo>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <CardTitle>Get Started</CardTitle>
            
            <ContactInfo>
              <InfoLabel>For Affiliates</InfoLabel>
              <InfoValue>
                Ready to start earning? Contact us to learn about our affiliate programs 
                and commission structures.
              </InfoValue>
            </ContactInfo>
            
            <ContactInfo>
              <InfoLabel>For Advertisers</InfoLabel>
              <InfoValue>
                Looking to expand in Eastern Asia? Let's discuss how our affiliate 
                network can drive growth for your business.
              </InfoValue>
            </ContactInfo>
            
            <ContactInfo>
              <InfoLabel>Partnership Opportunities</InfoLabel>
              <InfoValue>
                Interested in strategic partnerships? We're always open to exploring 
                mutually beneficial collaborations.
              </InfoValue>
            </ContactInfo>
          </ContactCard>
        </ContactGrid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CardTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Department Contacts
          </CardTitle>
          
          <DepartmentGrid>
            <DepartmentCard whileHover={{ scale: 1.05 }}>
              <DepartmentTitle>Business Development</DepartmentTitle>
              <DepartmentEmail>partnerships@growtech.cc</DepartmentEmail>
            </DepartmentCard>
            
            <DepartmentCard whileHover={{ scale: 1.05 }}>
              <DepartmentTitle>Affiliate Support</DepartmentTitle>
              <DepartmentEmail>affiliates@growtech.cc</DepartmentEmail>
            </DepartmentCard>
            
            <DepartmentCard whileHover={{ scale: 1.05 }}>
              <DepartmentTitle>Technical Support</DepartmentTitle>
              <DepartmentEmail>support@growtech.cc</DepartmentEmail>
            </DepartmentCard>
            
            <DepartmentCard whileHover={{ scale: 1.05 }}>
              <DepartmentTitle>Media & Press</DepartmentTitle>
              <DepartmentEmail>media@growtech.cc</DepartmentEmail>
            </DepartmentCard>
          </DepartmentGrid>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          style={{ marginTop: '4rem' }}
        >
          <BusinessHours>
            <HoursTitle>Business Hours</HoursTitle>
            <InfoValue>
              Our team operates across multiple time zones to serve our Eastern Asian markets effectively.
            </InfoValue>
            
            <HoursGrid>
              <TimeZone>
                <ZoneTitle>Hong Kong (HKT)</ZoneTitle>
                <ZoneTime>Monday - Friday<br />9:00 AM - 6:00 PM</ZoneTime>
              </TimeZone>
              
              <TimeZone>
                <ZoneTitle>Response Time</ZoneTitle>
                <ZoneTime>General Inquiries<br />Within 24 hours</ZoneTime>
              </TimeZone>
              
              <TimeZone>
                <ZoneTitle>Support Hours</ZoneTitle>
                <ZoneTime>Technical Support<br />Monday - Friday</ZoneTime>
              </TimeZone>
            </HoursGrid>
          </BusinessHours>
        </motion.div>
      </Container>
    </PageContainer>
  );
};

export default Contact; 